import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import patientBlisterService, {
  PatientBlisterSettings,
  UpdateBlisterConsentPayload,
  UpdatePatientBlisterPayload,
} from '../services/patientBlisterService';

/** One key for the whole surface, so the chip, the header cell and the dialog never disagree. */
export const patientBlisterQueryKey = (pharmacyId?: string, patientId?: string) =>
  ['patient-blister', pharmacyId, patientId] as const;

/**
 * PRX-46 — a patient's blister settings.
 *
 * ONE request per Patient 360 mount, for every patient. Whether a patient is on
 * blister packs is exactly what this read answers, so there is no cheaper
 * question to ask first; an unflagged patient simply renders no chip and no
 * header cell.
 *
 * Disabled until both ids are present — a request keyed on `undefined` would
 * cache one patient's settings under every other patient's key.
 */
export function usePatientBlister(pharmacyId?: string, patientId?: string) {
  return useQuery({
    queryKey: patientBlisterQueryKey(pharmacyId, patientId),
    queryFn: (): Promise<PatientBlisterSettings> =>
      patientBlisterService.get(pharmacyId as string, patientId as string),
    enabled: !!pharmacyId && !!patientId,
    // A patient with no PHOX row yet, or one the caller may not read, answers
    // 4xx — a settled answer, not a blip. Retrying it doubles the request for
    // every such patient and buys nothing. Anything else keeps the app default.
    retry: (failureCount, error) => {
      const status = (error as any)?.response?.status;
      if (typeof status === 'number' && status >= 400 && status < 500) return false;
      return failureCount < 1;
    },
  });
}

/**
 * The Patient 360 settings edit, and the "follow Fillware again" reversal.
 *
 * Invalidates the read on success so the chip and the header line repaint from
 * the server's answer rather than from what the dialog hoped it wrote — the
 * `followFillware` path in particular returns values the SYNC resolved, which
 * can differ from anything the client could have predicted.
 */
export function useUpdatePatientBlister(pharmacyId?: string, patientId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdatePatientBlisterPayload): Promise<PatientBlisterSettings> =>
      patientBlisterService.update(pharmacyId as string, patientId as string, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: patientBlisterQueryKey(pharmacyId, patientId) });
    },
  });
}

/**
 * PRX-127 — record blister packaging consent. The server's answer replaces the
 * cached settings, so the card shows what was stored, never what was hoped.
 */
export function useUpdateBlisterConsent(pharmacyId?: string, patientId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateBlisterConsentPayload): Promise<PatientBlisterSettings> =>
      patientBlisterService.updateConsent(pharmacyId as string, patientId as string, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(patientBlisterQueryKey(pharmacyId, patientId), data);
    },
  });
}

/**
 * WP-14 (PRX-141 Q5) — record the ODB frequent-dispensing exemption date. The
 * server's answer replaces the cached settings, as the consent does.
 */
export function useUpdateOdbExemption(pharmacyId?: string, patientId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { exemptionDate: string | null }): Promise<PatientBlisterSettings> =>
      patientBlisterService.updateOdbExemption(pharmacyId as string, patientId as string, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(patientBlisterQueryKey(pharmacyId, patientId), data);
    },
  });
}
